import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { GrFormAdd } from "react-icons/gr";
import { MdDeleteForever } from "react-icons/md";
import { BsCloudUpload } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import ActionButton from "../../../Common/ActionButton";
import ApproverPicker from "../../../Common/ApproverPicker";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { useSession } from "../../../../context/SessionContext";
import { filterStoreProjects } from "../../projectFilters";
import { getallMaterial } from "../../../../Redux/actions/Operationsactions/getmaterial";
import { getallStock } from "../../../../Redux/actions/Operationsactions/getstock";
import { saveDemandNote } from "./demandNoteService";
import "../../AddContractorsAndVendors.css";

const headerCellStyle = {
  height: "42px",
  paddingTop: "6px",
  paddingBottom: "6px",
  verticalAlign: "middle",
  lineHeight: "1.1",
};

const emptyLineItem = () => ({
  id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
  MaterialId: "",
  MaterialName: "",
  Specification: "",
  MaterialPackagingId: "",
  RequestedPackaging: "",
  EstStock: "—",
  QtyRequired: "",
  Unit: "",
  UsagePurpose: "",
});

const AddDemandNote = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { session, rolesdata } = useSession();

  const fileInputRef = useRef(null);

  // Redux data
  const materialRedux = useSelector((state) => state.getAllMaterial?.data || []);
  const stockRedux = useSelector((state) => state.getAllStock?.data || []);

  useEffect(() => {
    dispatch(getallMaterial());
    dispatch(getallStock());
  }, [dispatch]);

  // Projects list
  const projectList = useMemo(() => {
    return filterStoreProjects(rolesdata?.data?.Projects) || [];
  }, [rolesdata]);

  // Materials list
  const materialsList = useMemo(() => {
    if (materialRedux && materialRedux.length > 0) {
      return materialRedux.map((m) => {
        const stockItem = stockRedux.find((s) => s.MaterialId === m.Id);
        // Free stock (not reserved by approved requisitions), shown under the original "On-site Stock" label
        const estQty = stockItem
          ? `${stockItem.Free ?? stockItem.Quantity} ${m.UomName || ""}`
          : `0 ${m.UomName || ""}`;
        return {
          Id: m.Id,
          ItemName: m.ItemName,
          UomName: m.UomName || "Units",
          Packagings: m.Packagings || [],
          EstStock: estQty,
        };
      });
    }
    return [];
  }, [materialRedux, stockRedux]);

  const populateData = location.state || {};
  const isEdit = Boolean(populateData?.isEdit);

  const [isLoading, setIsLoading] = useState(false);

  // Form Header State
  const [formData, setFormData] = useState({
    DemandNoteNo: populateData.DemandNoteNo || "Generated on save",
    Priority: populateData.Priority || "Medium",
    ProjectId: populateData.ProjectId || "",
    RequisitionDate: populateData.RequisitionDate || moment().format("YYYY-MM-DD"),
    ExpectedDeliveryDate:
      populateData.ExpectedDeliveryDate || moment().add(5, "days").format("YYYY-MM-DD"),
    SpecificInstructions: populateData.SpecificInstructions || "",
    ProcurementAlert:
      populateData.ProcurementAlert !== undefined ? populateData.ProcurementAlert : true,
    FileName: populateData.FileName || "",
  });

  const [approverUserIds, setApproverUserIds] = useState(populateData.ApproverUserIds || []);

  // Line items state
  const [lineItems, setLineItems] = useState(() => {
    if (populateData.Items && populateData.Items.length > 0) {
      return populateData.Items.map((item) => ({
        ...item,
        id: item.id || `item-${Math.random()}`,
      }));
    }
    return [emptyLineItem()];
  });

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddRow = () => {
    setLineItems((prev) => [...prev, emptyLineItem()]);
  };

  const handleRemoveRow = (index) => {
    if (lineItems.length === 1) {
      toast.warning("A demand note must contain at least one line item.");
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleLineMaterialChange = (index, materialNameOrId) => {
    const matched = materialsList.find(
      (m) => m.ItemName === materialNameOrId || m.Id === materialNameOrId,
    );

    setLineItems((prev) => {
      const updated = [...prev];
      if (matched) {
        updated[index] = {
          ...updated[index],
          MaterialId: matched.Id,
          MaterialName: matched.ItemName,
          Unit: matched.UomName,
          MaterialPackagingId: "",
          RequestedPackaging: matched.UomName,
          EstStock: matched.EstStock || "—",
        };
      } else {
        updated[index] = {
          ...updated[index],
          MaterialId: "",
          MaterialName: materialNameOrId,
        };
      }
      return updated;
    });
  };

  const handlePackagingChange = (index, packagingId) => {
    const material = materialsList.find((m) => m.Id === lineItems[index].MaterialId);
    const pkg = (material?.Packagings || []).find((p) => p.Id === packagingId);
    const name = pkg ? pkg.PackagingName : material?.UomName || "";
    setLineItems((prev) =>
      prev.map((it, i) =>
        i === index
          ? { ...it, MaterialPackagingId: packagingId, RequestedPackaging: name, Unit: name }
          : it,
      ),
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, FileName: file.name }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.ProjectId) {
      toast.error("Please select a Requesting Site");
      return;
    }
    if (!formData.RequisitionDate) {
      toast.error("Please select Requisition Date");
      return;
    }
    if (!formData.ExpectedDeliveryDate) {
      toast.error("Please select Expected Delivery Date");
      return;
    }
    if (approverUserIds.length === 0) {
      toast.error("At least one Approver is required");
      return;
    }
    if (lineItems.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const row = lineItems[i];
      if (!row.MaterialId) {
        toast.error(`Please select a Material on row ${i + 1}`);
        return;
      }
      if (!row.QtyRequired || Number(row.QtyRequired) <= 0) {
        toast.error(`Valid Quantity is required on row ${i + 1}`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = {
        Id: isEdit ? populateData.Id : undefined,
        Priority: formData.Priority,
        ProjectId: formData.ProjectId,
        RequisitionDate: formData.RequisitionDate,
        ExpectedDeliveryDate: formData.ExpectedDeliveryDate,
        SpecificInstructions: formData.SpecificInstructions,
        ProcurementAlert: formData.ProcurementAlert,
        ApproverUserIds: approverUserIds,
        File: fileInputRef.current?.files?.[0] || null,
        Items: lineItems.map((item) => ({
          id: item.id,
          MaterialId: item.MaterialId,
          MaterialName: item.MaterialName,
          MaterialPackagingId: item.MaterialPackagingId,
          Specification: item.Specification,
          QtyRequired: Number(item.QtyRequired),
          UsagePurpose: item.UsagePurpose,
        })),
      };

      const res = await saveDemandNote(payload);
      if (!res.ok) {
        toast.error(res.message);
        return;
      }

      toast.success(
        isEdit ? "Demand Note updated successfully!" : "Demand Note created successfully!",
      );
      const listPath = history.location.pathname.startsWith("/store/material-requisition")
        ? "/store/material-requisition"
        : history.location.pathname.startsWith("/store/demand-note")
          ? "/store/demand-note"
          : history.location.pathname.startsWith("/procurement")
            ? "/procurement/demand-note"
            : "/store/demand-note";
      history.push(listPath);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save Demand Note");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">{isEdit ? "Edit" : "Add"} Demand Note</h4>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} autoComplete="off">
        {/* ── Demand Note Details ── */}
        {/* <h6 style={{ color: "#056363", marginTop: "14px" }}>Demand Note Details</h6> */}

        <div className="row mb-2">
          <InputField
            label="Demand Note No:"
            name="DemandNoteNo"
            value={formData.DemandNoteNo}
            disabled={true}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <SelectField
            label="Priority:"
            name="Priority"
            value={formData.Priority}
            onChange={handleFormChange}
            options={[
              { value: "Low", label: "Low" },
              { value: "Medium", label: "Medium" },
              { value: "High / Urgent", label: "High / Urgent" },
            ]}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <SelectField
            label="Requesting Site:"
            name="ProjectId"
            value={formData.ProjectId}
            onChange={handleFormChange}
            options={[
              { value: "", label: "Select Site" },
              ...projectList.map((p) => ({
                value: String(p.ProjectId),
                label: p.ProjectName,
              })),
            ]}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            required
          />

          <InputField
            label="Expected Delivery Date:"
            name="ExpectedDeliveryDate"
            type="date"
            value={formData.ExpectedDeliveryDate}
            onChange={handleFormChange}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <InputField
            label="Requisition Date:"
            name="RequisitionDate"
            type="date"
            value={formData.RequisitionDate}
            onChange={handleFormChange}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <InputField
            label="Requested By:"
            name="RequestedBy"
            value={isEdit ? populateData.RequestedBy : session?.UserName || ""}
            disabled={true}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
        </div>

        {/* ── Required Materials ── */}
        <h6 style={{ color: "#056363", marginTop: "24px" }}>Required Materials</h6>

        <div className="row mt-2 clearfix">
          <div className="col-md-12 p-0 column">
            <div className="table-responsive">
              <table
                className="table table-bordered table-hover"
                id="tab_logic"
                style={{ fontSize: "13px" }}
              >
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr style={{ height: "42px" }}>
                    <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                      Sr#
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "170px" }}>
                      Material
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "160px" }}>
                      Brand Preference
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "140px" }}>
                      Packaging
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "120px" }}>
                      On-site Stock
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "110px" }}>
                      Qty Required
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "110px" }}>
                      UOM
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "170px" }}>
                      Purpose
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                      <span onClick={handleAddRow} style={{ cursor: "pointer" }}>
                        <GrFormAdd className="add_icon drow" size="1.2em" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody id="tablebody">
                  {lineItems.map((item, idx) => (
                    <tr key={item.id || idx} id={`addr${idx}`}>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {idx + 1}
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <select
                          name="MaterialName"
                          value={item.MaterialId || ""}
                          onChange={(e) => handleLineMaterialChange(idx, e.target.value)}
                          className="input_styling"
                          required
                        >
                          <option value="">Select Material</option>
                          {materialsList.map((m) => (
                            <option key={m.Id} value={m.Id}>
                              {m.ItemName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <input
                          type="text"
                          name="Specification"
                          value={item.Specification || ""}
                          onChange={(e) => handleLineChange(idx, "Specification", e.target.value)}
                          className="input_styling"
                          placeholder="e.g. Bestway / Maple Leaf"
                        />
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <select
                          name="MaterialPackagingId"
                          value={item.MaterialPackagingId || ""}
                          onChange={(e) => handlePackagingChange(idx, e.target.value)}
                          className="input_styling"
                        >
                          <option value="">
                            {materialsList.find((m) => m.Id === item.MaterialId)?.UomName ||
                              "Select Material"}
                          </option>
                          {(
                            materialsList.find((m) => m.Id === item.MaterialId)?.Packagings || []
                          ).map((p) => (
                            <option key={p.Id} value={p.Id}>
                              {p.PackagingName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <input
                          type="text"
                          readOnly
                          className="input_styling text-center"
                          style={{
                            backgroundColor: "#f8f9fa",
                            cursor: "default",
                            fontWeight: 600,
                          }}
                          value={item.EstStock || "—"}
                        />
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <input
                          type="number"
                          name="QtyRequired"
                          value={item.QtyRequired ?? ""}
                          onChange={(e) => handleLineChange(idx, "QtyRequired", e.target.value)}
                          className="input_styling text-right"
                          placeholder="0"
                          min="0"
                          step="any"
                          required
                        />
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <input
                          type="text"
                          readOnly
                          className="input_styling text-center"
                          style={{
                            backgroundColor: "#f8f9fa",
                            cursor: "default",
                          }}
                          value={item.Unit || item.RequestedPackaging || "—"}
                        />
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <input
                          type="text"
                          name="UsagePurpose"
                          value={item.UsagePurpose || ""}
                          onChange={(e) => handleLineChange(idx, "UsagePurpose", e.target.value)}
                          className="input_styling"
                          placeholder="e.g. For 3rd floor column casting"
                        />
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <span onClick={() => handleRemoveRow(idx)}>
                          <MdDeleteForever
                            size="1.3em"
                            style={{ cursor: "pointer", color: "#dc3545" }}
                            title="Remove"
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Approval & Additional Details ── */}
        <h6 style={{ color: "#056363", marginTop: "24px" }}>Approval & Additional Details</h6>

        <div className="row mb-2">
          <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
            <label className="input_label d-block m-0 mb-2">Approvers:</label>
            <ApproverPicker
              projectId={formData.ProjectId}
              value={approverUserIds}
              onChange={setApproverUserIds}
            />
          </div>

          <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
            <label className="input_label d-block m-0 mb-2">Attach Specification:</label>
            <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} />
            {formData.FileName ? (
              <div className="document-upload-box justify-content-start">
                <BsCloudUpload className="document-upload-icon" />
                <div className="document-upload-file">
                  <span title={formData.FileName}>{formData.FileName}</span>
                  <button
                    type="button"
                    className="document-upload-remove"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, FileName: "" }));
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    title="Remove attachment"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="document-upload-box"
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <BsCloudUpload className="document-upload-icon" />
                <div className="document-upload-copy">
                  <strong>Upload specification</strong>
                  <span>Choose a file</span>
                </div>
              </div>
            )}
          </div>

          <div className="col-xl-3 col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
            <label className="input_label d-block m-0 mb-2">Procurement Alert:</label>
            <div className="d-flex align-items-center" style={{ minHeight: "38px" }}>
              <input
                type="checkbox"
                id="ProcurementAlert"
                name="ProcurementAlert"
                checked={formData.ProcurementAlert}
                onChange={handleFormChange}
                className="mr-2"
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <label
                htmlFor="ProcurementAlert"
                className="m-0"
                style={{ fontSize: "12px", cursor: "pointer" }}
              >
                Notify Procurement Team
              </label>
            </div>
          </div>

          <TextAreaField
            label="Specific Instructions:"
            name="SpecificInstructions"
            value={formData.SpecificInstructions}
            onChange={handleFormChange}
            rows={3}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
        </div>

        {/* ── Footer Buttons ── */}
        <div className="float-right mt-4 mr-md-3 mb-5">
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => {
              const listPath = history.location.pathname.startsWith("/store/material-requisition")
                ? "/store/material-requisition"
                : history.location.pathname.startsWith("/store/demand-note")
                  ? "/store/demand-note"
                  : history.location.pathname.startsWith("/procurement")
                    ? "/procurement/demand-note"
                    : "/store/demand-note";
              history.push(listPath);
            }}
          />
          <ActionButton
            text={isEdit ? "Update" : "Save"}
            icon={BsCloudUpload}
            type="submit"
            isLoading={isLoading}
          />
        </div>
      </form>
    </div>
  );
};

export { AddDemandNote };
export default AddDemandNote;
